import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";

/** =========================================================
 *  Slider-style Slideshow (warm palette)
 *  - Keeps EXACT same layout & API as before
 *  - Now uses a horizontal slider instead of cross-fade
 * ======================================================= */
const Slideshow = ({
  className = "",
  interval = 4000,
  fadeMs = 900,          // kept for compatibility (not used now)
  withOverlay = true,
  fallback = "/images/slideshow/1.jpg", // kept for compatibility (used as first slide)
}) => {
  const IMAGES = [1, 2, 3, 4, 5, 6, 7, 8].map((n) => `/images/slideshow/${n}.jpg`);

  const slides = IMAGES.length ? IMAGES : [fallback];

  return (
    <div
      className={`relative overflow-hidden rounded-b-[40px] ring-1 ring-black/10 shadow-xl ${className}`}
      aria-label="Slideshow"
    >
      <Swiper
        modules={[Autoplay, Pagination]}
        slidesPerView={1}
        loop={slides.length > 1}
        autoplay={{
          delay: 1500,
          disableOnInteraction: false,
        }}
        pagination={{ clickable: true }}
        className="h-full w-full"
      >
        {slides.map((src, idx) => (
          <SwiperSlide key={idx}>
            <img
              src={src}
              alt=""
              className="h-full w-full object-cover"
              draggable="false"
              onError={(e) => {
                e.currentTarget.src = fallback;
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Optional overlay for contrast on top of the slider */}
      {withOverlay && (
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
      )}
    </div>
  );
};

/** =========================================================
 *  Landing Section (Warm orange aesthetic)
 *  (layout unchanged, only slideshow behaviour changed)
 * ======================================================= */
const Landing = ({ onNavClick }) => {
  return (
    <section className="relative min-h-[100vh] bg-[#FFE8BA] overflow-hidden">
      {/* Soft warm vignette */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_100%_0%,rgba(245,158,11,0.12),transparent_60%),radial-gradient(60%_60%_at_0%_100%,rgba(234,88,12,0.12),transparent_65%)]" />

      <div className="mx-auto flex w-full max-w-7xl flex-col md:flex-row items-center justify-between gap-8 md:gap-10 px-5 sm:px-6 md:px-10 lg:px-12 py-10 md:py-16">
        {/* LEFT: Brand & Copy */}
        <div className="w-full md:w-[45%] lg:w-[42%] relative">
          <div className="rounded-3xl bg-white/0 backdrop-blur-sm ring-1 ring-amber-200 shadow-md px-6 md:px-7 lg:px-8 py-6 md:py-8">
            {/* Semi-circle behind logo (warm gradient) */}
            <div className="absolute top-8 left-1/2 -translate-x-1/2 w-[220px] md:w-[280px] lg:w-[320px] aspect-[2/1] rounded-t-full bg-gradient-to-b from-amber-200 via-orange-300 to-amber-400 opacity-80 -z-10" />

            <img
              src="/images/bantunotts_logo_black_.png"
              alt="Bantu Notts"
              className="mx-auto w-[260px] md:w-[320px] lg:w-[360px] select-none"
              draggable="false"
            />

            <h2 className="mt-6 text-center md:text-left font-extrabold tracking-tight text-[28px] md:text-[36px] lg:text-[44px] leading-[1.06] text-neutral-900">
              Bringing people together
              <span className="block text-neutral-700">through creativity.</span>
            </h2>

            <p className="mt-4 text-center md:text-left text-[18px] md:text-[20px] text-neutral-700">
              Community-led programmes, workshops, and digital experiences that
              connect, create, and cultivate.
            </p>

            <div className="mt-7 flex flex-wrap gap-3 justify-center md:justify-start">
              <button
                onClick={() => onNavClick?.("projects")}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 px-6 py-3 text-sm md:text-base font-semibold text-white shadow-md hover:opacity-90 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
                aria-label="Go to Projects"
              >
                Learn More
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.97 3.97a.75.75 0 011.06 0l6 6a.75.75 0 01-1.06 1.06L13.75 6.81v12.44a.75.75 0 01-1.5 0V6.81l-5.22 5.22a.75.75 0 11-1.06-1.06l6-6z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <button
                onClick={() => onNavClick?.("contact")}
                className="inline-flex items-center rounded-full px-5 py-3 text-sm md:text-base font-medium text-amber-800 ring-1 ring-amber-300 bg-amber-50 hover:bg-amber-100 transition"
                aria-label="Go to Contact"
              >
                Contact
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT: Nav + Slider */}
        <div className="w-full md:w-[55%]">
          <nav className="hidden md:flex flex-col text-black w-full select-none">
            <ul className="flex justify-between text-[26px] lg:text-[32px] font-semibold">
              <button
                className="hover:scale-[105%] hover:bg-orange-500 hover:text-white px-[10px] rounded-l transition"
                onClick={() => onNavClick?.("landing")}
              >
                HOME
              </button>
              <button
                className="hover:scale-[105%] hover:bg-orange-500 hover:text-white px-[10px] rounded-l transition"
                onClick={() => onNavClick?.("about")}
              >
                ABOUT
              </button>
              <button
                className="hover:scale-[105%] hover:bg-orange-500 hover:text-white px-[10px] rounded-l transition"
                onClick={() => onNavClick?.("projects")}
              >
                PROJECTS
              </button>
              <button
                className="hover:scale-[105%] hover:bg-orange-500 hover:text-white px-[10px] rounded-l transition"
                onClick={() => onNavClick?.("contact")}
              >
                CONTACT
              </button>
            </ul>
            <div className="hidden md:flex h-[1px] w-full mt-2 mb-4 bg-black/80" />
          </nav>

          <div className="rounded-b-[40px] overflow-hidden">
            {/* Same layout, now a sliding carousel instead of cross-fade */}
            <Slideshow className="h-[280px] sm:h-[340px] md:h-[70vh] lg:h-[72vh] w-full" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Landing;
